import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import * as Icons from "lucide-react";
import { Plus, Bell, Eye, ShoppingBag, Gem, Gift, PlayCircle, Sparkles, X, Volume2, ChevronUp, ChevronDown, Zap, Trophy } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, naira } from "@/lib/api";
import NaijaAvatar from "@/components/NaijaAvatar";

const SCENE = "https://static.prod-images.emergentagent.com/jobs/0305d694-6d38-4e56-9f10-ef46437aa1b3/images/311510388cd41f9ef90be17128b1dd73be71376a40c533c2347146f40582e434.jpeg";

const NEED_ICONS = {
  hunger: Icons.UtensilsCrossed, energy: Icons.Zap, fun: Icons.PartyPopper,
  social: Icons.MessageCircle, hygiene: Icons.Droplets, bladder: Icons.Toilet,
};
const STATUS = ["⚡ NEPA took light", "🚗 Go-slow for Third Mainland", "🔥 Fuel scarcity", "🎉 Owanbe season", "☀️ Lagos sun dey hot"];

function moodOf(needs) {
  const avg = Object.values(needs).reduce((a, b) => a + b, 0) / 6;
  if (avg > 70) return { t: "Very Happy", e: "😄", c: "#16a34a" };
  if (avg > 50) return { t: "Vibing", e: "😎", c: "#0891b2" };
  if (avg > 30) return { t: "Stressed", e: "😟", c: "#d97706" };
  return { t: "Suffering", e: "😭", c: "#dc2626" };
}

export default function Room() {
  const { character, setCharacter } = useAuth();
  const nav = useNavigate();
  const [state, setState] = useState(null);
  const [actions, setActions] = useState([]);
  const [unread, setUnread] = useState(0);
  const [sheet, setSheet] = useState(false);
  const [ad, setAd] = useState(null);
  const [busy, setBusy] = useState(null);
  const [clean, setClean] = useState(false);
  const [status] = useState(STATUS[Math.floor(Math.random() * STATUS.length)]);
  const [online] = useState(80000 + Math.floor(Math.random() * 60000));
  const [visits] = useState((12 + Math.random() * 6).toFixed(1));

  const load = useCallback(async () => {
    try {
      const [s, a, n] = await Promise.all([api.get("/game/state"), api.get("/actions"), api.get("/social/notifications")]);
      setState(s.data); setCharacter(s.data.character); setActions(a.data.actions); setUnread(n.data.unread);
    } catch (e) { toast.error(errMsg(e)); }
  }, [setCharacter]);

  useEffect(() => { load(); const t = setInterval(load, 25000); return () => clearInterval(t); }, [load]);

  const c = state?.character || character;

  const doAction = async (id, title) => {
    setBusy(id);
    try {
      const { data } = await api.post("/actions/do", { action_id: id });
      setCharacter(data.character); setState((s) => ({ ...s, character: data.character }));
      toast.success(title + " ✓");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };

  const claimDaily = async () => {
    try {
      const { data } = await api.post("/daily/claim");
      setCharacter(data.character); setState((s) => ({ ...s, character: data.character }));
      toast.success(`Daily: ${naira(data.reward)}${data.gems ? ` +${data.gems}💎` : ""} · Streak ${data.streak}🔥`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const startAd = () => setAd({ phase: "playing", count: 4 });
  useEffect(() => {
    if (!ad || ad.phase !== "playing") return;
    if (ad.count <= 0) {
      (async () => {
        try {
          const { data } = await api.post("/rewards/ad");
          setCharacter(data.character); setState((s) => ({ ...s, character: data.character }));
          setAd({ phase: "reward", reward: data.reward, left: data.ads_left });
        } catch (e) { toast.error(errMsg(e)); setAd(null); }
      })();
      return;
    }
    const t = setTimeout(() => setAd((a) => ({ ...a, count: a.count - 1 })), 1000);
    return () => clearTimeout(t);
  }, [ad, setCharacter]);

  if (!c) return null;
  const mood = moodOf(c.needs);
  const time = new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

  return (
    <div className="relative w-full h-[calc(100dvh-5.5rem)] overflow-hidden bg-[#cfe6d6]" data-testid="room-page">
      {/* Full-screen isometric room scene */}
      <img src={SCENE} alt="your room" draggable={false} className="absolute inset-0 w-full h-full object-cover select-none" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/20 pointer-events-none" />

      {/* Tap-scene zone to handle needs */}
      <button className="absolute inset-0 z-0" onClick={() => setSheet(true)} data-testid="room-scene" aria-label="handle needs" />

      {/* Your custom character standing in the crib */}
      <div className="absolute left-1/2 -translate-x-1/2 bottom-[20%] z-10 pointer-events-none" data-testid="room-character">
        <div className="absolute left-1/2 -translate-x-1/2 bottom-1 w-24 h-5 rounded-[50%] bg-black/25 blur-md" />
        <motion.div animate={{ y: [0, -4, 0] }} transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          style={{ filter: "drop-shadow(0 8px 10px rgba(0,0,0,0.35))" }}>
          <NaijaAvatar appearance={c.appearance} view="full" size={160} />
        </motion.div>
      </div>

      {/* Top floating cluster */}
      <AnimatePresence>
        {!clean && (
          <motion.div
            initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}
            className="absolute top-0 inset-x-0 z-20 px-3 pt-3 space-y-2"
          >
            {/* status bar */}
            <div className="bg-white/90 backdrop-blur rounded-2xl shadow-md px-2.5 py-2 flex items-center gap-2">
              <button onClick={() => nav("/me")} data-testid="room-avatar"
                className="w-10 h-10 rounded-full overflow-hidden bg-[#00c853]/10 flex items-end justify-center shrink-0" style={{ boxShadow: "0 0 0 2px #00c853" }}>
                <NaijaAvatar appearance={c.appearance} view="head" size={42} />
              </button>
              <div className="flex-1 min-w-0">
                <div className="font-display text-sm leading-tight truncate text-[#111]">{c.name}</div>
                <div className="flex items-center gap-1.5 text-xs">
                  <span className="text-slate-500">{time}</span>
                  <span style={{ color: mood.c }} className="font-semibold">{mood.e} {mood.t}</span>
                </div>
              </div>
              <Volume2 size={18} className="text-slate-500" />
              <button onClick={() => nav("/store")} data-testid="room-money" className="flex items-center gap-1 bg-[#eafaf0] rounded-full pl-2.5 pr-1 py-1">
                <span className="text-[#0a7d3b] font-bold text-xs">{naira(c.wallet)}</span>
                <span className="w-6 h-6 rounded-full bg-[#00c853] flex items-center justify-center"><Plus size={14} className="text-white" /></span>
              </button>
            </div>

            {/* chips row */}
            <div className="flex items-center gap-2">
              <div className="bg-white/85 backdrop-blur rounded-full px-2.5 py-1 text-xs font-semibold text-slate-400 flex items-center gap-1"><Eye size={12} className="text-slate-500" /> {visits}m visits</div>
              <div className="bg-white/85 backdrop-blur rounded-full px-2.5 py-1 text-xs font-semibold text-slate-400 flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-green-500" /> {(online/1000).toFixed(0)}k online</div>
              <button onClick={() => nav("/social")} className="relative ml-auto bg-white/90 backdrop-blur rounded-full w-9 h-9 flex items-center justify-center shadow-md" data-testid="room-notif">
                <Bell size={18} className="text-slate-500" />
                {unread > 0 && <span className="absolute -top-1 -right-1 bg-[#FF0055] text-white text-[0.6rem] font-bold rounded-full w-4 h-4 flex items-center justify-center">{unread}</span>}
              </button>
            </div>

            {/* event banner */}
            <div className="flex justify-center">
              <div className="bg-white/90 backdrop-blur rounded-full px-3.5 py-1 text-xs font-semibold text-slate-300 shadow">{status}</div>
            </div>

            {/* action cards */}
            <div className="space-y-2 pt-0.5">
              <ActionCard testid="room-rewards" onClick={() => nav("/rewards")} icon={<Zap size={18} className="text-white" />} bg="#f5a623" title="Hustler Rewards" sub={`${c.hustler_points ?? 0} points · play, build, get rewarded`} />
              <ActionCard testid="room-contests" onClick={() => nav("/contests")} icon={<Trophy size={18} className="text-white" />} bg="#FF0055" title="Skill Contests" sub="Compete & win prizes 🏆" />
              <ActionCard testid="room-buy" onClick={() => nav("/shop")} icon={<ShoppingBag size={18} className="text-white" />} bg="#00c853" title="Buy something new" sub="Flex fashion, cars & more" />
              <ActionCard testid="room-daily" onClick={claimDaily} icon={<Gift size={18} className="text-white" />} bg="#7c4dff" title="Daily reward" sub="Collect your streak bonus" />
              <ActionCard testid="room-freebonus" onClick={startAd} icon={<PlayCircle size={18} className="text-white" />} bg="#ff9100" title="Free gem hunt" sub="Watch a short ad for a bonus 🎁" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Clean-view toggle */}
      {clean ? (
        <button onClick={() => setClean(false)} data-testid="room-clean-toggle"
          className="absolute top-3 right-3 z-30 bg-black/45 backdrop-blur text-white text-xs font-semibold rounded-full px-3 py-1.5 flex items-center gap-1 shadow-lg">
          <ChevronDown size={14} /> Show panels
        </button>
      ) : (
        <button onClick={() => setClean(true)} data-testid="room-clean-toggle"
          className="absolute z-30 left-1/2 -translate-x-1/2 bottom-24 bg-black/45 backdrop-blur text-white text-xs font-semibold rounded-full px-3 py-1.5 flex items-center gap-1 shadow-lg">
          <ChevronUp size={14} /> Clean screen
        </button>
      )}

      {/* Bottom needs pill */}
      <div className="absolute bottom-3 inset-x-0 z-20 px-3">
        <div className="bg-white/92 backdrop-blur rounded-2xl shadow-lg p-2.5 flex items-center gap-3">
          <button onClick={() => nav("/me")}
            className="w-[42px] h-[42px] rounded-full overflow-hidden bg-[#00c853]/10 flex items-end justify-center shrink-0" style={{ boxShadow: "0 0 0 2px #00c853" }}>
            <NaijaAvatar appearance={c.appearance} view="head" size={44} />
          </button>
          <div className="flex-1 grid grid-cols-3 gap-x-3 gap-y-2">
            {Object.keys(NEED_ICONS).map((k) => {
              const Icon = NEED_ICONS[k];
              const v = Math.round(c.needs[k]);
              const seg = Math.round(v / 20);
              return (
                <div key={k} className="flex items-center gap-1" data-testid={`room-need-${k}`}>
                  <Icon size={13} className={v <= 20 ? "text-[#FF0055]" : "text-slate-500"} />
                  <div className="flex gap-0.5">
                    {[0, 1, 2, 3, 4].map((i) => (
                      <span key={i} className="w-1.5 h-3 rounded-sm" style={{ background: i < seg ? (v <= 20 ? "#FF0055" : "#00c853") : "#e2e2e2" }} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Needs action sheet */}
      <AnimatePresence>
        {sheet && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-40 bg-black/50" onClick={() => setSheet(false)} />
            <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", stiffness: 260, damping: 28 }}
              className="fixed bottom-0 inset-x-0 mx-auto w-full max-w-md z-50 bg-white rounded-t-3xl p-5 pb-8 max-h-[70dvh] overflow-y-auto no-scrollbar" data-testid="needs-sheet">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-display text-2xl text-[#111]">Handle your life</h2>
                <button data-testid="close-needs" onClick={() => setSheet(false)} className="p-2 rounded-full bg-zinc-100"><X size={18} className="text-slate-400" /></button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {actions.map((a) => {
                  const Icon = Icons[a.icon.split("-").map((s) => s[0].toUpperCase() + s.slice(1)).join("")] || Icons.Zap;
                  return (
                    <button key={a.id} data-testid={`room-action-${a.id}`} disabled={busy === a.id} onClick={() => doAction(a.id, a.title)}
                      className="bg-zinc-50 border border-zinc-200 rounded-xl p-3 text-left hover:border-[#00c853] transition-colors disabled:opacity-50">
                      <div className="flex items-center justify-between"><Icon size={18} className="text-[#00a846]" /><span className="text-xs text-slate-500">{a.cost ? naira(a.cost) : "Free"}</span></div>
                      <div className="mt-2 text-sm font-semibold text-[#111]">{a.title}</div>
                      <div className="text-[0.7rem] text-slate-500 mt-0.5 leading-tight">{a.desc}</div>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Rewarded ad overlay */}
      <AnimatePresence>
        {ad && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[60] bg-black flex flex-col items-center justify-center px-6" data-testid="ad-overlay">
            {ad.phase === "playing" ? (
              <>
                <div className="absolute top-4 right-4 text-xs text-slate-500">Sponsored · demo</div>
                <div className="w-full max-w-xs aspect-square rounded-2xl bg-gradient-to-br from-[#FF0055] via-[#7c4dff] to-[#00E5FF] flex flex-col items-center justify-center text-center p-6">
                  <motion.div animate={{ scale: [1, 1.12, 1] }} transition={{ duration: 1, repeat: Infinity }} className="text-6xl">📺</motion.div>
                  <div className="font-display text-2xl text-white mt-4">Your Ad Here</div>
                </div>
                <div className="mt-6 text-xs text-slate-500">Reward in {ad.count}s…</div>
              </>
            ) : (
              <motion.div initial={{ scale: 0.7, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="flex flex-col items-center text-center" data-testid="ad-reward">
                <Sparkles size={48} className="text-[#FFD700]" />
                <h2 className="font-display text-3xl mt-4 text-white">Free Bonus! 🎁</h2>
                <div className="mt-3 text-[#16A34A] font-display text-2xl">+{naira(ad.reward.cash)}</div>
                {ad.reward.gems > 0 && <div className="mt-1 text-[#FFD700] font-display text-lg flex items-center gap-1"><Gem size={16} /> +{ad.reward.gems} gems</div>}
                <div className="text-xs text-slate-500 mt-3">{ad.left} free bonus left today</div>
                <button data-testid="ad-claim" onClick={() => setAd(null)} className="mt-7 bg-[#00c853] text-white font-bold px-10 py-3 rounded-xl">Collect</button>
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function ActionCard({ testid, onClick, icon, bg, title, sub }) {
  return (
    <button data-testid={testid} onClick={onClick} className="w-full bg-white/90 backdrop-blur rounded-2xl shadow-md px-3 py-2.5 flex items-center gap-3 text-left active:scale-[0.99] transition-transform">
      <span className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: bg }}>{icon}</span>
      <div className="min-w-0"><div className="font-semibold text-sm text-[#111]">{title}</div><div className="text-xs text-slate-500 truncate">{sub}</div></div>
    </button>
  );
}
